import { deleteMeta, META_KEYS, readMeta, type Sealed, writeMeta } from './db.ts';

/**
 * Enkripsi cache lokal (T-P5-12, NFR-SEC-05).
 *
 * Data key AES-256-GCM dibuat sekali per perangkat dan tidak pernah disimpan
 * terbuka: yang tersimpan hanya bentuknya yang sudah dibungkus kunci turunan PIN
 * (PBKDF2) atau kunci hasil WebAuthn PRF. Konsekuensinya disengaja — perangkat
 * yang dicuri tidak dapat dibaca tanpa PIN atau biometrik pemiliknya, dan PIN
 * yang benar-benar lupa berarti cache harus diunduh ulang.
 *
 * Kunci disimpan sebagai `CryptoKey` non-extractable di memori proses; ia lenyap
 * saat tab ditutup dan tidak pernah dapat dibaca kembali oleh kode halaman.
 */

const PBKDF2_ITERATIONS = 310_000;
const KEY_ALGORITHM = { name: 'AES-GCM', length: 256 } as const;
const IV_BYTES = 12;
const SALT_BYTES = 16;

export type UnlockMethod = 'pin' | 'prf';

export interface WrappedKeyRecord {
  /** `pin` | `prf` — cara data key dibungkus. */
  method: UnlockMethod;
  /** Data key terbungkus, base64. */
  wrapped: string;
  iv: string;
  /** Salt PBKDF2; kosong untuk metode PRF. */
  salt: string;
  iterations: number;
  createdAt: string;
}

/**
 * Satu data key, beberapa pembungkus.
 *
 * PIN adalah pembungkus wajib; biometrik menambahkan pembungkus kedua atas kunci
 * yang **sama**. Dua data key terpisah akan membuat cache yang ditulis lewat
 * biometrik tidak terbaca lewat PIN — kegagalan yang baru terlihat saat sensor
 * sidik jari kebetulan tidak bekerja di lapangan.
 */
const WRAP_SLOTS: Record<UnlockMethod, string> = {
  pin: META_KEYS.wrappedKey,
  prf: `${META_KEYS.wrappedKey}.prf`,
};

let dataKey: CryptoKey | null = null;

function subtle(): SubtleCrypto {
  if (!globalThis.crypto?.subtle) {
    // HTTPS (atau localhost) adalah syarat WebCrypto; tanpanya seluruh cache
    // akan tersimpan terbuka, jadi lebih baik gagal terang-terangan.
    throw new Error('WebCrypto tidak tersedia — aplikasi harus dilayani lewat HTTPS');
  }
  return globalThis.crypto.subtle;
}

function randomBytes(length: number): Uint8Array {
  return globalThis.crypto.getRandomValues(new Uint8Array(length));
}

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** Kunci pembungkus dari PIN. Iterasi tinggi disengaja: PIN 6 digit mudah ditebak. */
async function keyFromPin(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const material = await subtle().importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveKey',
  ]);

  return subtle().deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    material,
    KEY_ALGORITHM,
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

/** Kunci pembungkus dari hasil WebAuthn PRF (biometrik). */
async function keyFromPrf(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  return subtle().importKey('raw', prfOutput, KEY_ALGORITHM, false, ['wrapKey', 'unwrapKey']);
}

export function isUnlocked(): boolean {
  return dataKey !== null;
}

export function lock(): void {
  dataKey = null;
}

/**
 * Buat data key baru dan simpan bentuk terbungkusnya.
 * Dipanggil sekali saat perangkat dipersiapkan, atau saat PIN diganti.
 */
export async function initializeDataKey(unlock: UnlockInput): Promise<void> {
  const key = await subtle().generateKey(KEY_ALGORITHM, true, ['encrypt', 'decrypt']);

  // Pembungkus lama membungkus data key yang sudah tidak dipakai; menyisakannya
  // berarti membuka kunci lewat biometrik menghasilkan kunci yang salah.
  for (const slot of Object.values(WRAP_SLOTS)) await deleteMeta(slot);
  await storeWrapped(key, unlock);

  // Kunci dipakai lagi dalam bentuk non-extractable supaya tidak bisa dibaca
  // kembali oleh kode halaman setelah dibungkus.
  dataKey = await reimportNonExtractable(key);
  await ensureSearchSalt();
}

export type UnlockInput = { method: 'pin'; pin: string } | { method: 'prf'; output: ArrayBuffer };

async function wrappingKey(unlock: UnlockInput, salt: Uint8Array): Promise<CryptoKey> {
  return unlock.method === 'pin' ? keyFromPin(unlock.pin, salt) : keyFromPrf(unlock.output);
}

async function storeWrapped(key: CryptoKey, unlock: UnlockInput): Promise<void> {
  const salt = unlock.method === 'pin' ? randomBytes(SALT_BYTES) : new Uint8Array();
  const iv = randomBytes(IV_BYTES);
  const wrapper = await wrappingKey(unlock, salt);

  const wrapped = await subtle().wrapKey('raw', key, wrapper, {
    name: 'AES-GCM',
    iv: iv as BufferSource,
  });

  const record: WrappedKeyRecord = {
    method: unlock.method,
    wrapped: toBase64(new Uint8Array(wrapped)),
    iv: toBase64(iv),
    salt: toBase64(salt),
    iterations: PBKDF2_ITERATIONS,
    createdAt: new Date().toISOString(),
  };

  await writeMeta(WRAP_SLOTS[unlock.method], record);
}

async function reimportNonExtractable(key: CryptoKey): Promise<CryptoKey> {
  const raw = await subtle().exportKey('raw', key);
  return subtle().importKey('raw', raw, KEY_ALGORITHM, false, ['encrypt', 'decrypt']);
}

/**
 * Buka data key. Mengembalikan `false` bila PIN salah — bukan melempar, karena
 * layar kunci menghitung percobaan gagal dan itu alur normalnya.
 */
export async function unlockDataKey(unlock: UnlockInput): Promise<boolean> {
  const key = await unwrapWith(unlock, false);
  dataKey = key;
  return key !== null;
}

async function unwrapWith(unlock: UnlockInput, extractable: boolean): Promise<CryptoKey | null> {
  const record = await readMeta<WrappedKeyRecord>(WRAP_SLOTS[unlock.method]);
  if (!record) return null;

  try {
    const wrapper = await wrappingKey(unlock, fromBase64(record.salt));
    return await subtle().unwrapKey(
      'raw',
      fromBase64(record.wrapped) as BufferSource,
      wrapper,
      { name: 'AES-GCM', iv: fromBase64(record.iv) as BufferSource },
      KEY_ALGORITHM,
      extractable,
      ['encrypt', 'decrypt'],
    );
  } catch {
    // GCM gagal terautentikasi = kunci pembungkus salah.
    return null;
  }
}

export async function hasWrapper(method: UnlockMethod): Promise<boolean> {
  return (await readMeta<WrappedKeyRecord>(WRAP_SLOTS[method])) !== undefined;
}

export async function removeWrapper(method: UnlockMethod): Promise<void> {
  await deleteMeta(WRAP_SLOTS[method]);
}

/**
 * Tambahkan cara membuka kunci tanpa mengganti data key.
 *
 * Kunci dibuka sementara dalam bentuk extractable — satu-satunya cara membungkus
 * ulang kunci yang sama — lalu handle-nya dilepas di akhir fungsi. Pemanggil
 * tetap harus membuktikan dirinya lewat `current`, jadi ini tidak membuka jalur
 * baru bagi siapa pun yang belum bisa membuka kunci.
 */
export async function addWrapper(current: UnlockInput, next: UnlockInput): Promise<boolean> {
  const extractableKey = await unwrapWith(current, true);
  if (!extractableKey) return false;

  await storeWrapped(extractableKey, next);
  return true;
}

/** Ganti PIN: bungkus ulang kunci yang sama dengan PIN baru. */
export async function replacePin(currentPin: string, nextPin: string): Promise<boolean> {
  return addWrapper({ method: 'pin', pin: currentPin }, { method: 'pin', pin: nextPin });
}

function requireKey(): CryptoKey {
  if (!dataKey) throw new Error('Cache lokal terkunci — buka dengan PIN atau biometrik');
  return dataKey;
}

/** `v1.<iv base64>.<ciphertext base64>` — versi ikut disimpan agar format bisa berubah. */
export async function sealField(value: string): Promise<Sealed> {
  const iv = randomBytes(IV_BYTES);
  const cipher = await subtle().encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    requireKey(),
    new TextEncoder().encode(value),
  );

  return `v1.${toBase64(iv)}.${toBase64(new Uint8Array(cipher))}`;
}

export async function openField(sealed: Sealed): Promise<string> {
  const [version, iv, cipher] = sealed.split('.');
  if (version !== 'v1' || !iv || !cipher) throw new Error('Format ciphertext lokal tidak dikenal');

  const plain = await subtle().decrypt(
    { name: 'AES-GCM', iv: fromBase64(iv) as BufferSource },
    requireKey(),
    fromBase64(cipher) as BufferSource,
  );

  return new TextDecoder().decode(plain);
}

export async function sealOptional(value: string | null): Promise<Sealed | null> {
  return value === null ? null : sealField(value);
}

export async function openOptional(value: Sealed | null): Promise<string | null> {
  return value === null ? null : openField(value);
}

/**
 * Salt pencarian — dipakai `search-tokens.ts`.
 *
 * Salt per perangkat membuat token dari dua perangkat tidak dapat dibandingkan,
 * sehingga basis data lokal yang bocor tidak bisa dipakai menguji tebakan nama
 * pada perangkat lain.
 */
export async function ensureSearchSalt(): Promise<Uint8Array> {
  const stored = await readMeta<string>(META_KEYS.searchSalt);
  if (stored) return fromBase64(stored);

  const salt = randomBytes(SALT_BYTES);
  await writeMeta(META_KEYS.searchSalt, toBase64(salt));
  return salt;
}
